import * as echarts from 'echarts'

const homeScatter = (data1, data2, data3, data4, data5) => {

  // =========================
  // species -> symbol
  // =========================
  const getSymbolBySpeciesType = (speciesType) => {
    const normalizedType = String(speciesType || '')
      .trim()
      .toLowerCase()

    if (normalizedType === 'animal') return 'triangle'

    return 'circle'
  }

  // =========================
  // format data
  // =========================
  const formatData = (data) => {
    return Array.isArray(data)
      ? data.map(item => {

          const rawDate = new Date(item[0])

          const dateStr = echarts.format.formatTime(
            'yyyy-MM-dd',
            rawDate
          )

          const baseData = [
            rawDate,
            item[1],
            item[2]?.replace('%', ''),
            item[3],
            item[4]
          ]

          const pointData = {
            value: baseData,
            symbol: getSymbolBySpeciesType(item[4])
          }

          // special offset
          if (
            dateStr === '2024-07-08' ||
            dateStr === '2024-11-04'
          ) {
            const offsetX = Math.floor(Math.random() * 30 - 15)
            const offsetY = Math.floor(Math.random() * 30 - 15)

            pointData.symbolOffset = [offsetX, offsetY]
          }

          return pointData
        })
      : []
  }

  const formattedData1 = formatData(data1)
  const formattedData2 = formatData(data2)
  const formattedData3 = formatData(data3)
  const formattedData4 = formatData(data4)
  const formattedData5 = formatData(data5)

  // =========================
  // common style
  // =========================
  const itemStyle = {
    opacity: 0.82,
    shadowBlur: 10,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowColor: 'rgba(0,0,0,0.25)'
  }

  // =========================
  // ploidy config
  // =========================
  const ploidySeries = [
    {
      name: 'Diploid',
      data: formattedData1,
      color: '#28a745'
    },
    {
      name: 'Triploid',
      data: formattedData2,
      color: '#409eff'
    },
    {
      name: 'Tetraploidy',
      data: formattedData3,
      color: '#f56c6c'
    },
    {
      name: 'Hexaploid',
      data: formattedData4,
      color: '#dfb154'
    },
    {
      name: 'Octoploid',
      data: formattedData5,
      color: '#706caa'
    }
  ]

  // =========================
  // generate series
  // =========================
  const seriesList = []

  ploidySeries.forEach(({ name, data, color }) => {

    if (!data.length) return

    // plant
    const plantData = data.filter(
      item =>
        getSymbolBySpeciesType(item.value?.[4]) === 'circle'
    )

    // animal
    const animalData = data.filter(
      item =>
        getSymbolBySpeciesType(item.value?.[4]) === 'triangle'
    )

    // plant series
    if (plantData.length) {
      seriesList.push({
        name: `${name} (Plant)`,

        type: 'scatter',

        symbol: 'circle',

        data: plantData,

        itemStyle: {
          ...itemStyle,
          color
        },

        emphasis: {
          scale: true
        }
      })
    }

    // animal series
    if (animalData.length) {
      seriesList.push({
        name: `${name} (Animal)`,

        type: 'scatter',

        symbol: 'triangle',

        data: animalData,

        itemStyle: {
          ...itemStyle,
          color
        },

        emphasis: {
          scale: true
        }
      })
    }

  })

  // =========================
  // option
  // =========================
  const option = {

    color: [
      '#28a745',
      '#409eff',
      '#f56c6c',
      '#dfb154',
      '#706caa'
    ],

    // =========================
    // legend
    // =========================
    legend: {

      top: 10,
      left: 'center',

      itemWidth: 14,
      itemHeight: 14,
      itemGap: 18,

      textStyle: {
        fontSize: 16,
        color: '#606266'
      },

      selectedMode: true
    },

    // =========================
    // grid
    // =========================
    grid: {
      left: '10%',
      right: 150,
      top: '18%',
      bottom: '10%'
    },

    // =========================
    // tooltip
    // =========================
    tooltip: {

      backgroundColor: 'rgba(255,255,255,0.92)',

      borderColor: '#ebeef5',

      textStyle: {
        color: '#303133'
      },

      formatter: function (param) {

        const value = param.value

        const formattedName =
          value[3]?.replace(/_/g, ' ') || ''

        return `
          <div style="font-size:16px;font-weight:600;margin-bottom:6px;">
            <i>${formattedName}</i>
          </div>

          <div>Genome size: ${value[1]} Mb</div>

          <div>T2T/all rate: ${value[2]}%</div>

          <div>Species type: ${value[4] || ''}</div>

          <div>
            Release date:
            ${echarts.format.formatTime('yyyy-MM-dd', value[0])}
          </div>
        `
      }
    },

    // =========================
    // xAxis
    // =========================
    xAxis: {

      type: 'time',

      name: 'Release date',

      nameGap: 16,

      nameTextStyle: {
        fontSize: 20
      },

      splitLine: {
        show: true
      },

      axisLabel: {
        formatter: function (value) {
          return echarts.format.formatTime(
            'yyyy-MM-dd',
            value
          )
        }
      }
    },

    // =========================
    // yAxis
    // =========================
    yAxis: {

      type: 'log',

      logBase: 3,

      name: 'Genome size',

      nameLocation: 'center',

      nameGap: 60,

      nameTextStyle: {
        fontSize: 20
      },

      splitLine: {
        show: false
      },

      axisLabel: {
        formatter: function (val) {

          if (val >= 1000) {
            return (val / 1024).toFixed(1) + 'G'
          }

          return val + 'M'
        }
      }
    },

    // =========================
    // zoom
    // =========================
    dataZoom: [
      {
        type: 'inside'
      },

      {
        type: 'slider',

        fillerColor: 'rgba(46, 50, 56, 0.05)',

        borderColor: 'rgba(46, 50, 56, 0)',

        textStyle: {
          color: 'rgba(28, 31, 35, 0.6)',
          fontFamily: 'Inter'
        },

        dataBackground: {
          areaStyle: {
            color: 'rgba(46, 50, 56, 0.09)'
          }
        },

        handleIcon:
          'path://M15,30C15,21.71270751953125,21.71270751953125,15,30,15C38.28729248046875,15,45,21.71270751953125,45,30C45,38.28729248046875,38.28729248046875,45,30,45C21.71270751953125,45,15,38.28729248046875,15,30Z',

        handleStyle: {
          color: '#fff',
          shadowColor: 'rgba(0, 0, 0, 0.1)',
          shadowBlur: 6
        }
      }
    ],

    // =========================
    // visualMap
    // =========================
    visualMap: [
      {
        left: 'right',

        top: '10%',

        dimension: 2,

        min: 0,

        max: 100,

        itemWidth: 30,

        itemHeight: 120,

        calculable: true,

        precision: 0.1,

        text: ['% T2T rate'],

        inRange: {
          symbolSize: [0, 30]
        },

        outOfRange: {
          symbolSize: [30, 50],
          color: ['rgba(255,255,255,0.4)']
        },

        controller: {
          inRange: {
            color: ['#28a745']
          },

          outOfRange: {
            color: ['#999']
          }
        }
      }
    ],

    // =========================
    // series
    // =========================
    series: seriesList
  }

  return option
}

export default homeScatter